/**
 * ChatWidget Component
 * Real-time-ish messaging with support team using polling
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { chatAPI } from '../../../utils/api';

const styles = {
  container: {
    padding: '40px 20px',
    marginBottom: '40px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: '24px',
  },
  chatBox: {
    maxWidth: '600px',
    margin: '0 auto',
    borderRadius: '8px',
    border: '1px solid #2A3444',
    backgroundColor: '#1A2332',
    display: 'flex',
    flexDirection: 'column',
    height: '500px',
    overflow: 'hidden',
  },
  messageArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  message: {
    display: 'flex',
    marginBottom: '12px',
    animation: 'slideIn 0.3s ease-in-out',
  },
  messageProspect: {
    justifyContent: 'flex-end',
  },
  messageSupport: {
    justifyContent: 'flex-start',
  },
  messageBubble: {
    maxWidth: '70%',
    padding: '12px 16px',
    borderRadius: '8px',
    wordWrap: 'break-word',
    fontSize: '14px',
    lineHeight: '1.4',
  },
  messageBubbleProspect: {
    backgroundColor: '#0057FF',
    color: '#FFFFFF',
    borderBottomRightRadius: '2px',
  },
  messageBubbleSupport: {
    backgroundColor: '#2A3444',
    color: '#FFFFFF',
    borderBottomLeftRadius: '2px',
  },
  messageTime: {
    fontSize: '12px',
    color: '#888',
    marginTop: '4px',
    textAlign: 'right',
  },
  inputArea: {
    padding: '16px',
    borderTop: '1px solid #2A3444',
    display: 'flex',
    gap: '12px',
    backgroundColor: '#0F1729',
  },
  input: {
    flex: 1,
    padding: '12px 16px',
    borderRadius: '6px',
    border: '1px solid #2A3444',
    backgroundColor: '#1A2332',
    color: '#FFFFFF',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  inputFocus: {
    borderColor: '#0057FF',
    outline: 'none',
    boxShadow: '0 0 0 2px rgba(0, 87, 255, 0.1)',
  },
  sendButton: {
    padding: '12px 24px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#0057FF',
    color: '#FFFFFF',
    fontWeight: '600',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  sendButtonHover: {
    backgroundColor: '#003D99',
  },
  sendButtonDisabled: {
    backgroundColor: '#2A3444',
    cursor: 'not-allowed',
    opacity: 0.5,
  },
  header: {
    padding: '16px 20px',
    borderBottom: '1px solid #2A3444',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  headerTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#FFFFFF',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#00D4AA',
  },
  statusDotOffline: {
    backgroundColor: '#888',
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#BBBEC7',
    textAlign: 'center',
    padding: '20px',
  },
  loadingSpinner: {
    display: 'inline-block',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#0057FF',
    marginRight: '8px',
    animation: 'blink 1.4s ease-in-out infinite',
  },
};

/**
 * ChatWidget Component
 */
function ChatWidget({ prospect }) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const messageAreaRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const lastMessageTimeRef = useRef(Date.now());

  // Fetch initial messages
  useEffect(() => {
    if (!prospect?.id) return;

    const loadMessages = async () => {
      try {
        setIsLoading(true);
        const response = await chatAPI.getMessages(prospect.id, 50);
        setMessages(response.messages || []);
        lastMessageTimeRef.current = Date.now();
      } catch (err) {
        console.error('Failed to load messages:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadMessages();
  }, [prospect?.id]);

  // Poll for new messages every 3 seconds
  useEffect(() => {
    if (!prospect?.id) return;

    const poll = async () => {
      try {
        const response = await chatAPI.pollNewMessages(
          prospect.id,
          new Date(lastMessageTimeRef.current).toISOString()
        );

        if (response.messages && response.messages.length > 0) {
          setMessages((prev) => [...prev, ...response.messages]);
          lastMessageTimeRef.current = Date.now();
        }
      } catch (err) {
        console.warn('Failed to poll for messages:', err);
      }
    };

    pollIntervalRef.current = setInterval(poll, 3000);
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [prospect?.id]);

  // Auto-scroll to latest message
  useEffect(() => {
    if (messageAreaRef.current) {
      messageAreaRef.current.scrollTop = messageAreaRef.current.scrollHeight;
    }
  }, [messages]);

  // Add CSS animations if not already in page
  useEffect(() => {
    if (!document.querySelector('style[data-chat-animations]')) {
      const style = document.createElement('style');
      style.setAttribute('data-chat-animations', 'true');
      style.textContent = `
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes blink {
          0%, 20%, 50%, 80%, 100% {
            opacity: 1;
          }
          40% {
            opacity: 0.5;
          }
          60% {
            opacity: 0.5;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Handle sending a message
  const handleSendMessage = useCallback(async () => {
    if (!inputValue.trim() || !prospect?.id || isSending) return;

    const messageText = inputValue.trim();
    setInputValue('');
    setIsSending(true);

    // Optimistically add message to UI
    const tempMessage = {
      id: `temp-${Date.now()}`,
      text: messageText,
      senderName: 'You',
      timestamp: new Date().toISOString(),
      isFromProspect: true,
    };
    setMessages((prev) => [...prev, tempMessage]);

    try {
      const response = await chatAPI.sendMessage(prospect.id, messageText, 'Prospect');
      lastMessageTimeRef.current = Date.now();

      // Replace temp message with real one if it has an ID
      if (response.message?.id) {
        setMessages((prev) =>
          prev.map((msg) => (msg.id === tempMessage.id ? response.message : msg))
        );
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      // Remove the temp message on failure
      setMessages((prev) => prev.filter((msg) => msg.id !== tempMessage.id));
      setInputValue(messageText); // Restore input
    } finally {
      setIsSending(false);
    }
  }, [inputValue, prospect?.id, isSending]);

  // Handle enter key to send
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!prospect?.id) {
    return null;
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Chat with Our Team</h2>

      <div style={styles.chatBox}>
        {/* Header */}
        <div style={styles.header}>
          <div style={{ ...styles.statusDot, ...(isOnline ? {} : styles.statusDotOffline) }} />
          <div style={styles.headerTitle}>
            {isOnline ? 'Support Team' : 'Offline'}
          </div>
        </div>

        {/* Messages */}
        <div style={styles.messageArea} ref={messageAreaRef}>
          {isLoading ? (
            <div style={styles.emptyState}>
              <div style={styles.loadingSpinner} />
              Loading messages...
            </div>
          ) : messages.length === 0 ? (
            <div style={styles.emptyState}>
              No messages yet. Send a message to start a conversation!
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                style={{
                  ...styles.message,
                  ...(msg.isFromProspect ? styles.messageProspect : styles.messageSupport),
                }}
              >
                <div>
                  <div
                    style={{
                      ...styles.messageBubble,
                      ...(msg.isFromProspect
                        ? styles.messageBubbleProspect
                        : styles.messageBubbleSupport),
                    }}
                  >
                    {msg.text}
                  </div>
                  <div style={styles.messageTime}>
                    {new Date(msg.timestamp).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Input */}
        <div style={styles.inputArea}>
          <input
            style={styles.input}
            type="text"
            placeholder="Type your message..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isSending}
          />
          <button
            style={{
              ...styles.sendButton,
              ...(isSending && styles.sendButtonDisabled),
            }}
            onClick={handleSendMessage}
            disabled={isSending || !inputValue.trim()}
            onMouseEnter={(e) => {
              if (!isSending && inputValue.trim()) {
                e.target.style.backgroundColor = '#003D99';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSending) {
                e.target.style.backgroundColor = '#0057FF';
              }
            }}
          >
            {isSending ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

ChatWidget.propTypes = {
  prospect: function (props, propName) {
    if (props[propName] != null && typeof props[propName] !== 'object') {
      return new Error(`${propName} must be an object`);
    }
  },
};

export default ChatWidget;
